import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  Edit,
  X,
  Layers,
  Plus,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface FeeTier {
  id: string;
  name: string;
  display_name: string;
  tier_level: number;
  min_volume_30d: number | null;
  trading_fee_rate: number;
  referral_commission_l1: number;
  referral_commission_l2: number;
  is_active: boolean;
}

interface EditModalProps {
  tier: FeeTier | null;
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
}

function EditTierModal({ tier, onClose, onSave, isLoading }: EditModalProps) {
  const [formData, setFormData] = useState({
    name: tier?.name || '',
    display_name: tier?.display_name || '',
    tier_level: tier?.tier_level?.toString() || '',
    min_volume_30d: tier?.min_volume_30d?.toString() || '',
    trading_fee_rate: tier?.trading_fee_rate?.toString() || '',
    referral_commission_l1: tier?.referral_commission_l1?.toString() || '',
    referral_commission_l2: tier?.referral_commission_l2?.toString() || '',
    is_active: tier?.is_active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      display_name: formData.display_name,
      tier_level: parseInt(formData.tier_level),
      min_volume_30d: formData.min_volume_30d ? parseFloat(formData.min_volume_30d) : null,
      trading_fee_rate: parseFloat(formData.trading_fee_rate),
      referral_commission_l1: parseFloat(formData.referral_commission_l1),
      referral_commission_l2: parseFloat(formData.referral_commission_l2),
      is_active: formData.is_active,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-white">
            {tier ? 'Edit Fee Tier' : 'Create Fee Tier'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Internal Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., gold"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Gold"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Tier Level</label>
              <input
                type="number"
                min="0"
                value={formData.tier_level}
                onChange={(e) => setFormData({ ...formData, tier_level: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Min Volume (30d)</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={formData.min_volume_30d}
                onChange={(e) => setFormData({ ...formData, min_volume_30d: e.target.value })}
                placeholder="Leave empty for manual"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              Trading Fee Rate (e.g., 0.001 = 0.1%)
            </label>
            <input
              type="number"
              step="0.00001"
              min="0"
              max="0.1"
              value={formData.trading_fee_rate}
              onChange={(e) => setFormData({ ...formData, trading_fee_rate: e.target.value })}
              placeholder="0.001"
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">L1 Commission (e.g., 0.05 = 5%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={formData.referral_commission_l1}
                onChange={(e) => setFormData({ ...formData, referral_commission_l1: e.target.value })}
                placeholder="0.05"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">L2 Commission (e.g., 0.02 = 2%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={formData.referral_commission_l2}
                onChange={(e) => setFormData({ ...formData, referral_commission_l2: e.target.value })}
                placeholder="0.02"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 bg-black border border-white/10"
            />
            <label htmlFor="is_active" className="text-xs text-zinc-400">
              Tier is active
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FeeTiers() {
  const queryClient = useQueryClient();
  const [editingTier, setEditingTier] = useState<FeeTier | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-fee-tiers'],
    queryFn: () => apiRequest('GET', '/api/admin/fee-tiers'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiRequest('PUT', `/api/admin/fee-tiers/${id}`, data),
    onSuccess: () => {
      toast.success('Fee tier updated successfully');
      setEditingTier(null);
      queryClient.invalidateQueries({ queryKey: ['admin-fee-tiers'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update fee tier');
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/api/admin/fee-tiers', data),
    onSuccess: () => {
      toast.success('Fee tier created successfully');
      setIsCreating(false);
      queryClient.invalidateQueries({ queryKey: ['admin-fee-tiers'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create fee tier');
    },
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Fee Tiers</h1>
          <p className="text-sm text-zinc-500">Manage trading fee tiers and referral rates</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-3 h-3" />
          Add Tier
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Tier</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Level</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Min Volume</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Trading Fee</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">L1 Commission</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">L2 Commission</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Status</th>
                <th className="text-right text-xs text-zinc-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.tiers?.map((tier: FeeTier) => (
                <tr key={tier.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-zinc-500" />
                      <div>
                        <p className="text-sm text-white">{tier.display_name}</p>
                        <p className="text-xs text-zinc-500">{tier.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-300">
                    {tier.tier_level}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-300">
                    {tier.min_volume_30d
                      ? `$${tier.min_volume_30d.toLocaleString()}`
                      : <span className="text-zinc-500">Manual</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-emerald-400">
                      {(tier.trading_fee_rate * 100).toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-blue-400">
                      {(tier.referral_commission_l1 * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-blue-400">
                      {(tier.referral_commission_l2 * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 ${
                      tier.is_active
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {tier.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditingTier(tier)}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editingTier && (
        <EditTierModal
          tier={editingTier}
          onClose={() => setEditingTier(null)}
          onSave={(data) => updateMutation.mutate({ id: editingTier.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Create Modal */}
      {isCreating && (
        <EditTierModal
          tier={null}
          onClose={() => setIsCreating(false)}
          onSave={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}
    </div>
  );
}
